import { WelcomeContentDetailsEntity } from '../das-finsuite/admin-components/AdminModels';
import { FinanceCalendarEventEntity } from '../das-finsuite/widgets/finance-calendar-widget/FinanceCalendarData';
import { AnnouncementFlatTableEntity, ApplicationFlatTableEntity } from '../context/AppContextModels';

// --- Search result types ---

export interface SearchResultItem {
  id: string;
  category: 'products' | 'announcements' | 'pages';
  title: string;
  description?: string;
  isFavorite?: boolean;
  href?: string;
  meta?: string;
  external?: boolean;
  /** Whether the application is embedded within FinSuite (products only) */
  embeddingEnabled?: boolean;
  /** The GL account / module category (products only) */
  glAccount?: string;
}

export const MAX_RESULTS_PER_CATEGORY = 5;

// --- Quick Links (side nav + profile dropdown) ---

export interface QuickLinkItem {
  id: string;
  text: string;
  href: string;
  external?: boolean;
  section?: string;
}

// --- Search logic ---

const matchesQuery = (text: string | undefined | null, query: string): boolean => {
  if (!text) return false;
  return text.toLowerCase().includes(query);
};

export const searchApplications = (apps: ApplicationFlatTableEntity[], query: string, favoriteIds: string[]): SearchResultItem[] => {
  return apps
    .filter((app) => matchesQuery(app.applicationName, query) || matchesQuery(app.shortApplicationDescription, query))
    .map((app) => ({
      id: app.id,
      category: 'products' as const,
      title: app.applicationName,
      description: app.shortApplicationDescription,
      isFavorite: favoriteIds.includes(app.id),
      href: app.embeddingEnabled ? `/all-apps/${app.id}` : app.applicationLink || undefined,
      embeddingEnabled: app.embeddingEnabled,
      glAccount: app.glAccount
    }));
};

export const searchAnnouncements = (announcements: AnnouncementFlatTableEntity[], query: string): SearchResultItem[] => {
  return announcements
    .filter((a) => a.isActive && (matchesQuery(a.title, query) || matchesQuery(a.announcement, query) || matchesQuery(a.applicationName, query)))
    .map((a) => ({
      id: a.id,
      category: 'announcements' as const,
      title: a.title,
      description: a.applicationName,
      meta: a.announcementDate,
      href: a.announcementLink || undefined
    }));
};

export const searchQuickLinks = (links: QuickLinkItem[], query: string): SearchResultItem[] => {
  return links
    .filter((link) => matchesQuery(link.text, query) || matchesQuery(link.section, query))
    .map((link) => ({
      id: link.id,
      category: 'pages' as const,
      title: link.text,
      description: link.section,
      href: link.href,
      external: link.external
    }));
};

export const searchWelcomeContent = (items: WelcomeContentDetailsEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.displayText && (matchesQuery(item.displayText, query) || matchesQuery(item.description, query)))
    .map((item) => ({
      id: `welcome-${item.id || item.displayText}`,
      category: 'pages' as const,
      title: item.displayText!,
      description: item.description || undefined,
      href: item.link || undefined,
      external: !!item.link
    }));
};

export const searchFinanceCalendar = (items: FinanceCalendarEventEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.event && (matchesQuery(item.event, query) || matchesQuery(item.description, query) || matchesQuery(item.scenario, query)))
    .map((item) => ({
      id: `calendar-${item.event}`,
      category: 'pages' as const,
      title: item.event,
      description: item.description || undefined,
      meta: item.scenario
    }));
};

export interface GroupedSearchResults {
  products: SearchResultItem[];
  announcements: SearchResultItem[];
  pages: SearchResultItem[];
}

export const performSearch = (
  query: string,
  apps: ApplicationFlatTableEntity[],
  announcements: AnnouncementFlatTableEntity[],
  favoriteIds: string[],
  quickLinks: QuickLinkItem[],
  welcomeContent: WelcomeContentDetailsEntity[],
  financeCalendar: FinanceCalendarEventEntity[]
): GroupedSearchResults => {
  const q = query.toLowerCase().trim();
  if (!q) return { products: [], announcements: [], pages: [] };

  return {
    products: searchApplications(apps, q, favoriteIds),
    announcements: searchAnnouncements(announcements, q),
    pages: [...searchQuickLinks(quickLinks, q), ...searchWelcomeContent(welcomeContent, q), ...searchFinanceCalendar(financeCalendar, q)]
  };
};
